import React, { useEffect, useRef, useState } from "react";
import { Package, MapPin, Clock, Mail, Truck, Eye } from "lucide-react";
import orderAPI from "../../api/orderApi";
import ShipmentDetail from "../../pages/orders/ShipmentDetail";
import { Link } from "react-router-dom";
import { useNotification } from "../../context/NotificationContext";

function OrderReceiveModal() {
  const { orders, setOrders } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const hasClosedModalRef = useRef(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const offcanvasRef = useRef(null);

  useEffect(() => {
    if (orders.length > 0 && !hasClosedModalRef.current) {
      setShowModal(true);
    }
  }, [orders]);

  const handleCloseModal = () => {
    setShowModal(false);
    hasClosedModalRef.current = true; // 🔥 important
  };

  useEffect(() => {
    if (selectedOrder && offcanvasRef.current) {
      const bsOffcanvas = window.bootstrap?.Offcanvas.getOrCreateInstance(
        offcanvasRef.current,
      );
      bsOffcanvas?.show();
    }
  }, [selectedOrder]);

  const handleShipmentAdded = (orderId) => {
    if (offcanvasRef.current) {
      const bsOffcanvas = window.bootstrap?.Offcanvas.getInstance(
        offcanvasRef.current,
      );
      bsOffcanvas?.hide();
    }

    setSelectedOrder(null);

    setOrders((prev) => prev.filter((o) => o._id !== orderId));

    // ✅ optional: agar sab orders complete ho gaye
    if (orders.length === 1) {
      setShowModal(false);
    }
  };

  return (
    <>
      {showModal && (
        <div
          className="modal fade show standard-modal"
          id="standard-modal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="standard-modalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              {/* Header */}
              <div className="modal-header bg-success text-white">
                <div className="d-flex align-items-center gap-2">
                  <div>
                    <h4 className="modal-title fw-bold mb-0 text-white">
                      New Orders!
                    </h4>
                    <small>{orders.length} order(s) pending shipment</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseModal}
                />
              </div>

              <div className="modal-body">
                {orders.map((order) => (
                  <OrderCard
                    key={order._id}
                    order={order}
                    onAddShipment={() => setSelectedOrder(order)}
                    onViewDetails={handleCloseModal}
                  />
                ))}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Minimize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="shipmentOffcanvas"
        ref={offcanvasRef}
        style={{ width: "420px" }}
      >
        {selectedOrder && (
          <ShipmentDetail
            item={selectedOrder}
            onSuccess={handleShipmentAdded}
          />
        )}
      </div>
    </>
  );
}

function OrderCard({ order, onAddShipment, onViewDetails }) {
  const customer = order?.userId;
  const address = order?.address;

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      {/* Top row */}
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <span className="fw-bold">#{order.orderId}</span>
          <span className="badge bg-warning text-dark ms-2">Processing</span>
        </div>
        <small className="text-muted">
          <Clock size={13} className="me-1" />
          {new Date(order.createdAt).toLocaleString("en-IN")}
        </small>
      </div>

      {/* Customer */}
      <div className="row mb-2">
        <div className="col-md-6">
          <small className="text-muted d-flex align-items-center gap-1">
            <Package size={13} /> Customer
          </small>
          <div className="fw-semibold">
            {customer?.firstName} {customer?.lastName}
          </div>
        </div>
        <div className="col-md-6">
          <small className="text-muted d-flex align-items-center gap-1">
            <Mail size={13} /> Email
          </small>
          <div className="fw-semibold">{customer?.email}</div>
        </div>
      </div>

      {address && (
        <div className="mb-2">
          <small className="text-muted d-flex align-items-center gap-1">
            <MapPin size={13} /> Address
          </small>
          <div className="fw-semibold small">
            {address.addressLine1}, {address.addressLine2}, {address.district},{" "}
            {address.state} - {address.pinCode}
          </div>
        </div>
      )}

      {order.items?.length > 0 &&
        order.items.map((item) => (
          <div
            key={item._id}
            className="d-flex justify-content-between align-items-center border rounded p-2 mb-2 bg-white"
          >
            <div className="d-flex align-items-center">
              <a
                href={item.thumbnail}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={item.thumbnail || "/placeholder.png"}
                  alt={item.productName}
                  className="me-2"
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 6,
                  }}
                />
              </a>

              <div>
                <div className="fw-semibold">
                  {item.productName || "Product"}{" "}
                  {item.variantName ? `- ${item.variantName}` : ""}
                </div>
                <small className="text-muted">Qty: {item.quantity}</small>
              </div>
            </div>

            <div className="text-end">
              <div className="fw-bold text-dark">
                ₹{item.total || item.price * item.quantity}
              </div>
              <small className="text-muted">₹{item.price} each</small>
            </div>
          </div>
        ))}

      {/* Total + Action */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <span className="fw-bold text-primary d-flex align-items-center gap-1">
          ₹{order.total}
        </span>

        <div>
          <button
            className="btn btn-sm btn-primary align-items-center"
            onClick={onAddShipment}
            data-bs-toggle="offcanvas"
            data-bs-target="#shipmentOffcanvas"
          >
            <Truck size={15} /> Add Shipment
          </button>

          <Link
            to={`/orders/details/${order.orderId}`}
            className="btn btn-primary btn-sm ms-2"
            onClick={onViewDetails}
          >
            <Eye size={15} />
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
}

export default OrderReceiveModal;
